import { useEffect, useMemo, useRef } from "react";
import { useInboxMessages } from "@/hooks/useInboxMessages";
import { useInboxLocalStorageSync } from "@/hooks/useInboxLocalStorageSync";
import { useMailLiveSync } from "@/hooks/useMailLiveSync";
import {
  MAIL_NEW_MAIL_EVENT,
  type MailNewMailDetail,
} from "@/lib/mail/mailNewMailEvents";
import type { MailFolder } from "@/lib/types/mail";

type Options = {
  folders?: MailFolder[];
  enabled?: boolean;
  pauseImapWhileReading?: boolean;
  /** Bloque toute sync IMAP headers (ex. brouillon en cours d'enregistrement). */
  pauseImapFully?: boolean;
  /** Badges dossiers (ex. nouveau mail en INBOX pendant lecture d’un autre dossier). */
  onFolderBadgesRefresh?: () => void;
};

/**
 * Orchestrateur unique : liste (React + DD02), sync fond, pastilles via useInboxBodyCacheFlags.
 */
export function useMailInboxSync(
  accountId?: number,
  folder = "INBOX",
  searchQuery = "",
  searchInput = "",
  unreadOnly = false,
  options: Options = {}
) {
  const inbox = useInboxMessages(accountId, folder, searchQuery, searchInput, unreadOnly);

  useMailLiveSync({
    accountId,
    folder,
    folders: options.folders ?? [],
    enabled: options.enabled ?? true,
    pauseImapWhileReading: options.pauseImapWhileReading ?? false,
    pauseImapFully: options.pauseImapFully ?? false,
  });

  const onFolderBadgesRefreshRef = useRef(options.onFolderBadgesRefresh);
  onFolderBadgesRefreshRef.current = options.onFolderBadgesRefresh;

  useEffect(() => {
    if (accountId == null || options.enabled === false) return;

    const onNewMail = (event: Event) => {
      const detail = (event as CustomEvent<MailNewMailDetail>).detail;
      if (!detail || detail.accountId !== accountId || detail.newCount < 1) return;
      onFolderBadgesRefreshRef.current?.();
    };

    window.addEventListener(MAIL_NEW_MAIL_EVENT, onNewMail);
    return () => window.removeEventListener(MAIL_NEW_MAIL_EVENT, onNewMail);
  }, [accountId, options.enabled]);

  const storageUids = useMemo(
    () => inbox.messages.map((m) => m.uid).filter((uid) => uid > 0),
    [inbox.messages]
  );

  useInboxLocalStorageSync(accountId, folder, storageUids, options.enabled !== false);

  return inbox;
}
